// useGuidedEngine — the reusable engine behind every Guided walkthrough.
//
// Extracted from the original GuidedDemo so EVERY "New …/Add …" entry point can
// offer the same presenter-style guide, each with its own steps[]. The engine
// owns: the chat driver (open/connect/send + turn-idle tracking), ground-truth
// readers (storages/strategies/mappings/target rows), live page refresh, route
// navigation, and the step runner + autoplay state machine.
//
// A guide is just an array of step objects:
//   { title, lines[], run(ctx)?, body(ctx)?, autoAdvanceReady(ctx)? }
// run() drives the real UI + chat and WAITS until it verifies the real result.
// autoAdvanceReady(ctx) gates autoplay (e.g. wait for the user to type a name or
// pick a file); default true.
import { useCallback, useEffect, useRef, useState } from "react";

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Derive a safe sql-ish identifier from a filename.
export function ident(name, fallback) {
  const base = String(name || "").split("/").pop().replace(/\.[a-z0-9]+$/i, "");
  const s = base.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 50);
  return s || fallback;
}
export function slugify(name) {
  let s = String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 63);
  if (s && !/^[a-z]/.test(s)) s = "p_" + s;
  return s;
}

// ── chat driver (module-level; no React state needed) ───────────────────────
export function chatIframe() { return document.querySelector('iframe[src*="chat.html"]'); }
export function postToChat(msg) {
  const f = chatIframe();
  if (f && f.contentWindow) { f.contentWindow.postMessage(msg, "*"); return true; }
  return false;
}
export function openChat() {
  window.dispatchEvent(new CustomEvent("cortex:open-harness", { detail: { harness: "orchestrator" } }));
}

export function useGuidedEngine({ navigate } = {}) {
  const [status, setStatus] = useState("");
  const [running, setRunning] = useState(false);

  // Broadcast a global data-changed event so whatever step page is shown re-fetches
  // and reflects live progress as the chat builds entities.
  const refreshPages = useCallback(() => {
    try { window.dispatchEvent(new CustomEvent("cortex:data-changed")); } catch {}
  }, []);
  const goPage = useCallback((path) => {
    try { if (navigate) { navigate(path); return; } } catch {}
    try { window.history.pushState({}, "", path); window.dispatchEvent(new PopStateEvent("popstate")); } catch {}
  }, [navigate]);

  // Tick a data-changed broadcast every few seconds so visible pages keep
  // refreshing during long-running chat builds (while the engine is mounted).
  useEffect(() => {
    const t = setInterval(() => { try { window.dispatchEvent(new CustomEvent("cortex:data-changed")); } catch {} }, 4000);
    return () => clearInterval(t);
  }, []);

  // turn-idle tracking — the headless agent rejects a send while a turn is live.
  const busyRef = useRef(false);
  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type === "chat-turn-start") busyRef.current = true;
      if (e.data?.type === "chat-turn-end") busyRef.current = false;
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const waitChatIdle = useCallback(async (capMs = 240_000) => {
    const t0 = Date.now();
    await sleep(800);
    while (Date.now() - t0 < capMs) { if (!busyRef.current) return true; await sleep(1500); }
    return false;
  }, []);
  const waitChatConnected = useCallback(async (capMs = 60_000) => {
    const t0 = Date.now();
    while (Date.now() - t0 < capMs) {
      const f = chatIframe();
      try {
        const doc = f && f.contentDocument;
        if (doc && doc.getElementById("status-bar")?.classList.contains("connected")) return true;
      } catch { /* not ready */ }
      await sleep(800);
    }
    return false;
  }, []);
  const chatSend = useCallback(async (text) => {
    openChat();
    for (let i = 0; i < 40 && !chatIframe(); i++) await sleep(500);
    await waitChatConnected();
    await waitChatIdle();
    postToChat({ type: "chat-send", text });
    await sleep(1800);
  }, [waitChatIdle, waitChatConnected]);
  // Drive one chat instruction until `check()` is true (or timeout); re-nudges the
  // agent up to 4× if it goes idle without satisfying the check.
  const chatStep = useCallback(async (text, check, { totalMs = 360_000, statusFn } = {}) => {
    if (await check()) return true;
    await chatSend(text);
    const t0 = Date.now();
    let sends = 1;
    while (Date.now() - t0 < totalMs) {
      await sleep(5000);
      if (statusFn) setStatus(await statusFn(Math.round((Date.now() - t0) / 1000)));
      if (await check()) return true;
      if (!busyRef.current && sends < 4) {
        if (await check()) return true;
        postToChat({ type: "chat-send", text: text + " (please proceed — call the tools directly now)" });
        await sleep(1500); sends++;
      }
    }
    return await check();
  }, [chatSend]);

  // Force the chat to reconnect a fresh session pointed at a project (used after
  // a project switch so tool calls run against the right project).
  const reconnectChat = useCallback(async (slug, branch = "main") => {
    openChat();
    for (let i = 0; i < 20 && !chatIframe(); i++) await sleep(500);
    postToChat({ type: "chat-reconnect", activeProject: slug, activeBranch: branch, token: localStorage.getItem("access_token") || "" });
    await sleep(1500);
  }, []);

  return {
    status, setStatus, running, setRunning,
    refreshPages, goPage,
    waitChatIdle, waitChatConnected, chatSend, chatStep, reconnectChat,
    postToChat, openChat, busyRef,
  };
}
